import { useEffect, useState } from 'react';
import { C } from '../data';
import { supabase } from '../supabase';
import { useCart } from '../context/CartContext';
import { redeemCoupon, spendPointsForOrder } from '../api';

export default function PaymentResultPage({ onGoHome }) {
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading | success | fail
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // 네이버페이 콜백 파라미터
    const resultCode     = params.get('resultCode');
    const paymentId      = params.get('paymentId');
    const merchantPayKey = params.get('merchantPayKey');

    // 토스 콜백 파라미터
    const paymentKey = params.get('paymentKey');
    const orderId    = params.get('orderId');
    const amount     = params.get('amount');

    if (resultCode !== null && merchantPayKey) {
      handleNaverPayResult(resultCode, paymentId, merchantPayKey);
    } else if (paymentKey && orderId && amount) {
      confirmPayment(paymentKey, orderId, Number(amount));
    } else {
      setErrorMsg(params.get('message') || '결제가 취소되었습니다.');
      setStatus('fail');
    }
  }, []); // eslint-disable-line

  const handleNaverPayResult = async (resultCode, paymentId, merchantPayKey) => {
    if (resultCode !== '00') {
      setErrorMsg('결제가 취소되었거나 실패했습니다.');
      setStatus('fail');
      return;
    }
    try {
      const raw = sessionStorage.getItem('pendingNaverOrder');
      if (raw) {
        const o = JSON.parse(raw);
        const { error: orderErr } = await supabase.from('orders').insert({
          id: o.orderId,
          user_id: o.userId,
          items: o.items,
          shipping: o.shipping,
          total: o.grandTotal,
          status: 'confirmed',
          discount: o.discount,
          coupon_code: o.couponCode || null,
          payment_method: 'naverpay',
        });
        if (orderErr) throw orderErr;
        if (o.userCouponId) { await redeemCoupon(o.userCouponId, o.orderId).catch(() => {}); }
        if (o.pointsToUse > 0) { await spendPointsForOrder(o.orderId, o.pointsToUse).catch(() => {}); }
        sessionStorage.removeItem('pendingNaverOrder');
        if (!o.isBuyNow) { await clearCart(); }
      }
      window.history.replaceState({}, '', '/payment/naver-result');
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('fail');
    }
  };

  const confirmPayment = async (paymentKey, orderId, amount) => {
    try {
      const { data, error } = await supabase.functions.invoke('confirm-payment', {
        body: { paymentKey, orderId, amount },
      });
      if (error) throw new Error('Edge Function 오류: ' + JSON.stringify(error));
      if (!data?.success) throw new Error(data?.message || '결제 확인에 실패했습니다.');

      // 결제 확인 후 주문 DB insert
      const raw = sessionStorage.getItem('pendingOrder');
      if (raw) {
        const o = JSON.parse(raw);
        await supabase.from('orders').insert({
          id: o.orderId,
          user_id: o.userId,
          items: o.items,
          shipping: o.shipping,
          total: o.grandTotal,
          status: 'confirmed',
          discount: o.discount,
          coupon_code: o.couponCode || null,
          payment_method: 'card',
        });
        if (o.userCouponId) { await redeemCoupon(o.userCouponId, o.orderId).catch(() => {}); }
        if (o.pointsToUse > 0) { await spendPointsForOrder(o.orderId, o.pointsToUse).catch(() => {}); }
        sessionStorage.removeItem('pendingOrder');
      }

      await clearCart();
      window.history.replaceState({}, '', '/payment/success');
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('fail');
    }
  };

  if (status === 'loading') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:C.muted, letterSpacing:2 }}>결제 확인 중...</span>
    </div>
  );

  if (status === 'success') return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:24, padding:40 }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5">
        <circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>
      </svg>
      <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:32, letterSpacing:2 }}>결제 완료</span>
      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:C.muted, textAlign:'center', lineHeight:2.2 }}>
        주문이 접수되었습니다.<br/>확인 후 배송 안내 이메일을 드리겠습니다.
      </p>
      <button onClick={onGoHome} style={{ padding:'12px 40px', background:C.ink, color:C.bg, border:'none', cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3 }}>
        홈으로
      </button>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:24, padding:40 }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#C94040" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/>
      </svg>
      <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:32, letterSpacing:2 }}>결제 실패</span>
      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:C.muted, textAlign:'center', lineHeight:2.2 }}>
        {errorMsg}
      </p>
      <button onClick={onGoHome} style={{ padding:'12px 40px', background:C.ink, color:C.bg, border:'none', cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3 }}>
        홈으로
      </button>
    </div>
  );
}
